import { auth } from "../../frontend/lib/firebase.ts";

interface LocationLike {
  origin: string;
  port: string;
  protocol: string;
  hostname: string;
}

function getLocation(): LocationLike | null {
  const candidate = (globalThis as { location?: LocationLike }).location;
  return candidate ?? null;
}

const locationRef = getLocation();
const API_BASE_URL = locationRef ? locationRef.origin : "http://localhost:3000";

export function getApiBaseUrl(): string {
  return API_BASE_URL;
}

function resolveUrl(pathOrUrl: string): URL {
  return new URL(pathOrUrl, API_BASE_URL);
}

async function getFirebaseIdToken(): Promise<string> {
  if (!auth?.currentUser) return "";
  try {
    return await auth.currentUser.getIdToken();
  } catch {
    return "";
  }
}

export async function apiRequest(pathOrUrl: string, init: RequestInit = {}): Promise<Response> {
  const url = resolveUrl(pathOrUrl);
  const headers = new Headers(init.headers);
  const bearerToken = await getFirebaseIdToken();

  if (bearerToken) {
    headers.set("Authorization", `Bearer ${bearerToken}`);
  }

  return fetch(url.toString(), {
    ...init,
    headers,
  });
}

function normalizeErrorMessage(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== "object") {
    return fallback;
  }

  const payloadRecord = payload as Record<string, unknown>;
  const payloadError = payloadRecord.error;
  if (typeof payloadError === "string") {
    return payloadError;
  }

  if (payloadError && typeof payloadError === "object") {
    const errorRecord = payloadError as Record<string, unknown>;
    if (typeof errorRecord.message === "string") {
      return errorRecord.message;
    }
  }

  if (typeof payloadRecord.message === "string") {
    return payloadRecord.message;
  }

  return fallback;
}

export async function apiJson<T>(pathOrUrl: string, init: RequestInit = {}): Promise<T> {
  const response = await apiRequest(pathOrUrl, init);
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(
      normalizeErrorMessage(payload, `Request failed with status ${response.status}`)
    );
  }

  return payload as T;
}

export interface ApiStreamEvent<T = unknown> {
  event: string;
  data: T | null;
  rawData: string;
}

function parseStreamBlock<T>(block: string): ApiStreamEvent<T> | null {
  const lines = block.split(/\r?\n/);
  let event = "message";
  const dataLines: string[] = [];

  for (const line of lines) {
    if (!line || line.startsWith(":")) continue;

    const separatorIndex = line.indexOf(":");
    const field = separatorIndex >= 0 ? line.slice(0, separatorIndex) : line;
    let value = separatorIndex >= 0 ? line.slice(separatorIndex + 1) : "";
    if (value.startsWith(" ")) {
      value = value.slice(1);
    }

    if (field === "event") {
      event = value || "message";
      continue;
    }

    if (field === "data") {
      dataLines.push(value);
    }
  }

  if (dataLines.length === 0) {
    return null;
  }

  const rawData = dataLines.join("\n");
  let parsed: T | null = null;

  try {
    parsed = JSON.parse(rawData) as T;
  } catch {
    parsed = rawData as T;
  }

  return {
    event,
    data: parsed,
    rawData,
  };
}

export async function streamApiEvents<T>(
  pathOrUrl: string,
  options: {
    signal?: AbortSignal;
    onEvent: (event: ApiStreamEvent<T>) => void;
  }
): Promise<void> {
  const response = await apiRequest(pathOrUrl, {
    headers: {
      Accept: "text/event-stream",
    },
    signal: options.signal,
  });

  if (!response.ok) {
    const errorPayload = await response.json().catch(() => null);
    throw new Error(
      normalizeErrorMessage(errorPayload, `Request failed with status ${response.status}`)
    );
  }

  if (!response.body) {
    throw new Error("Streaming response body is not available");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const flushBuffer = (final = false) => {
    const delimiter = "\n\n";
    let boundaryIndex = buffer.indexOf(delimiter);

    while (boundaryIndex >= 0) {
      const block = buffer.slice(0, boundaryIndex).trim();
      buffer = buffer.slice(boundaryIndex + delimiter.length);
      if (block) {
        const event = parseStreamBlock<T>(block);
        if (event) {
          options.onEvent(event);
        }
      }
      boundaryIndex = buffer.indexOf(delimiter);
    }

    if (final) {
      const block = buffer.trim();
      buffer = "";
      if (!block) return;
      const event = parseStreamBlock<T>(block);
      if (event) {
        options.onEvent(event);
      }
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      flushBuffer();
    }

    buffer += decoder.decode();
    flushBuffer(true);
  } finally {
    reader.releaseLock();
  }
}
